/**
 * ModelPerformanceBadge - Consolidated Model Performance Display
 * Unified component for displaying AI/ML model metrics across the application
 * Reduces duplication and provides consistent model performance visualization
 */

import { useState } from 'react'
import clsx from 'clsx'
import {
  Brain,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle,
  AlertTriangle,
  Target,
  Activity,
  RefreshCw,
  Clock
} from 'lucide-react'

// ============================================================================
// TYPES
// ============================================================================

type DisplayVariant = 'badge' | 'compact' | 'card' | 'detailed'

interface ModelMetrics {
  accuracy: number
  precision?: number
  recall?: number
  f1Score?: number
  auc?: number
}

interface ModelPerformanceBadgeProps {
  /** Model name (e.g., "Anomaly Detection", "Failure Prediction") */
  modelName?: string
  /** Model version */
  version?: string
  /** Primary accuracy metric */
  accuracy: number
  /** Optional additional metrics */
  metrics?: ModelMetrics
  /** Confidence level (0-1) */
  confidence?: number
  /** Display variant */
  variant?: DisplayVariant
  /** Trend direction vs previous period */
  trend?: 'up' | 'down' | 'stable'
  /** Trend value (percentage change) */
  trendValue?: number
  /** Last updated timestamp */
  lastUpdated?: string
  /** Number of predictions made */
  predictionCount?: number
  /** Show expandable details */
  expandable?: boolean
  /** Custom thresholds */
  thresholds?: {
    excellent: number
    good: number
    fair: number
  }
  className?: string
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getPerformanceLevel(
  accuracy: number,
  thresholds = { excellent: 0.90, good: 0.80, fair: 0.70 }
): {
  level: 'excellent' | 'good' | 'fair' | 'poor'
  label: string
  color: string
  bgColor: string
  borderColor: string
} {
  if (accuracy >= thresholds.excellent) {
    return {
      level: 'excellent',
      label: 'Excellent',
      color: 'text-emerald-400',
      bgColor: 'bg-emerald-500/20',
      borderColor: 'border-emerald-500/30'
    }
  }
  if (accuracy >= thresholds.good) {
    return {
      level: 'good',
      label: 'Good',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
      borderColor: 'border-blue-500/30'
    }
  }
  if (accuracy >= thresholds.fair) {
    return {
      level: 'fair',
      label: 'Fair',
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/20',
      borderColor: 'border-amber-500/30'
    }
  }
  return {
    level: 'poor',
    label: 'Needs Attention',
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/30'
  }
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/** Mini metric display */
function MetricPill({
  label,
  value,
  threshold = 0.85
}: {
  label: string
  value: number
  threshold?: number
}) {
  const isGood = value >= threshold

  return (
    <div className={clsx(
      'px-3 py-1.5 rounded-lg border text-center',
      isGood
        ? 'bg-emerald-500/10 border-emerald-500/20'
        : 'bg-amber-500/10 border-amber-500/20'
    )}>
      <p className="text-[10px] text-surface-400 uppercase tracking-wider">{label}</p>
      <p className={clsx(
        'text-sm font-mono font-bold',
        isGood ? 'text-emerald-400' : 'text-amber-400'
      )}>
        {(value * 100).toFixed(1)}%
      </p>
    </div>
  )
}

/** Circular progress indicator */
function AccuracyRing({
  accuracy,
  size = 64
}: {
  accuracy: number
  size?: number
}) {
  const performance = getPerformanceLevel(accuracy)
  const radius = (size - 8) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (accuracy * circumference)

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          className="text-surface-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={performance.color}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={clsx('text-sm font-bold font-mono', performance.color)}>
          {(accuracy * 100).toFixed(0)}%
        </span>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ModelPerformanceBadge({
  modelName = 'AI Model',
  version,
  accuracy,
  metrics,
  confidence,
  variant = 'badge',
  trend,
  trendValue,
  lastUpdated,
  predictionCount,
  expandable = false,
  thresholds,
  className
}: ModelPerformanceBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const performance = getPerformanceLevel(accuracy, thresholds)

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus

  // Badge variant - minimal inline display
  if (variant === 'badge') {
    return (
      <div className={clsx(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border',
        performance.bgColor,
        performance.borderColor,
        className
      )}>
        <Brain size={14} className={performance.color} />
        <span className={clsx('text-sm font-mono font-bold', performance.color)}>
          {(accuracy * 100).toFixed(1)}%
        </span>
        {trend && (
          <TrendIcon size={12} className={
            trend === 'up' ? 'text-emerald-400' :
            trend === 'down' ? 'text-red-400' : 'text-surface-500'
          } />
        )}
      </div>
    )
  }

  // Compact variant - small card format
  if (variant === 'compact') {
    return (
      <div className={clsx(
        'flex items-center gap-3 p-3 rounded-lg border bg-surface-800/50',
        performance.borderColor,
        className
      )}>
        <AccuracyRing accuracy={accuracy} size={48} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs text-surface-400">{modelName}</span>
            {version && (
              <span className="text-[10px] text-surface-500 font-mono">v{version}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={clsx('text-sm font-semibold', performance.color)}>
              {performance.label}
            </span>
            {trend && trendValue !== undefined && (
              <span className={clsx(
                'text-xs flex items-center gap-0.5',
                trend === 'up' ? 'text-emerald-400' :
                trend === 'down' ? 'text-red-400' : 'text-surface-500'
              )}>
                <TrendIcon size={10} />
                {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}{Math.abs(trendValue)}%
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Card variant - medium detail display
  if (variant === 'card') {
    return (
      <div className={clsx(
        'p-4 rounded-xl border bg-surface-800/50',
        performance.borderColor,
        className
      )}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={clsx('p-2 rounded-lg', performance.bgColor)}>
              <Brain size={20} className={performance.color} />
            </div>
            <div>
              <h4 className="font-semibold text-surface-200">{modelName}</h4>
              <div className="flex items-center gap-2 mt-0.5">
                {version && (
                  <span className="text-xs text-surface-500 font-mono">v{version}</span>
                )}
                {lastUpdated && (
                  <span className="text-xs text-surface-500 flex items-center gap-1">
                    <Clock size={10} />
                    {lastUpdated}
                  </span>
                )}
              </div>
            </div>
          </div>
          <AccuracyRing accuracy={accuracy} size={56} />
        </div>

        <div className={clsx(
          'flex items-center justify-between p-3 rounded-lg',
          performance.bgColor
        )}>
          <div className="flex items-center gap-2">
            {performance.level === 'excellent' || performance.level === 'good' ? (
              <CheckCircle size={16} className={performance.color} />
            ) : (
              <AlertTriangle size={16} className={performance.color} />
            )}
            <span className={clsx('font-medium', performance.color)}>
              {performance.label} Performance
            </span>
          </div>
          {trend && trendValue !== undefined && (
            <div className={clsx(
              'flex items-center gap-1 text-sm',
              trend === 'up' ? 'text-emerald-400' :
              trend === 'down' ? 'text-red-400' : 'text-surface-500'
            )}>
              <TrendIcon size={14} />
              <span className="font-mono">{trend === 'up' ? '+' : ''}{trendValue}%</span>
            </div>
          )}
        </div>

        {metrics && (
          <div className="grid grid-cols-4 gap-2 mt-4">
            <MetricPill label="Accuracy" value={accuracy} />
            {metrics.precision && <MetricPill label="Precision" value={metrics.precision} />}
            {metrics.recall && <MetricPill label="Recall" value={metrics.recall} />}
            {metrics.f1Score && <MetricPill label="F1 Score" value={metrics.f1Score} />}
          </div>
        )}
      </div>
    )
  }

  // Detailed variant - full display with optional expansion
  return (
    <div className={clsx(
      'rounded-xl border-2 overflow-hidden bg-surface-900',
      performance.borderColor,
      className
    )}>
      {/* Header */}
      <div className={clsx('p-5', performance.bgColor)}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className={clsx('p-3 rounded-xl', performance.bgColor, 'border', performance.borderColor)}>
              <Brain size={28} className={performance.color} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">{modelName}</h3>
                {version && (
                  <span className="text-xs font-mono text-surface-400 bg-surface-800/50 px-2 py-0.5 rounded">
                    v{version}
                  </span>
                )}
              </div>
              <p className="text-sm text-surface-400 mt-1">
                {performance.label} - {(accuracy * 100).toFixed(1)}% accuracy
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <AccuracyRing accuracy={accuracy} size={72} />
            {confidence !== undefined && (
              <div className="text-center px-3 py-2 bg-surface-800/50 rounded-lg border border-surface-700">
                <p className="text-[10px] text-surface-500 uppercase">Confidence</p>
                <p className={clsx(
                  'text-xl font-bold font-mono',
                  confidence >= 0.85 ? 'text-emerald-400' :
                  confidence >= 0.70 ? 'text-amber-400' : 'text-red-400'
                )}>
                  {Math.round(confidence * 100)}%
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Trend and stats */}
        <div className="flex items-center gap-4 mt-4 pt-4 border-t border-surface-700/30">
          {trend && trendValue !== undefined && (
            <div className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg',
              trend === 'up' ? 'bg-emerald-500/20 text-emerald-400' :
              trend === 'down' ? 'bg-red-500/20 text-red-400' :
              'bg-surface-700 text-surface-400'
            )}>
              <TrendIcon size={14} />
              <span className="text-sm font-medium">
                {trend === 'up' ? '+' : trend === 'down' ? '-' : ''}{Math.abs(trendValue)}% vs last period
              </span>
            </div>
          )}
          {predictionCount !== undefined && (
            <div className="flex items-center gap-2 text-sm text-surface-400">
              <Activity size={14} />
              <span>{predictionCount.toLocaleString()} predictions</span>
            </div>
          )}
          {lastUpdated && (
            <div className="flex items-center gap-2 text-sm text-surface-400">
              <RefreshCw size={14} />
              <span>Updated {lastUpdated}</span>
            </div>
          )}
        </div>
      </div>

      {/* Metrics Grid */}
      {metrics && (
        <div className="p-5 border-t border-surface-700/50">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} className="text-primary-400" />
            <h4 className="font-semibold text-surface-200">Performance Metrics</h4>
          </div>
          <div className="grid grid-cols-5 gap-3">
            <MetricPill label="Accuracy" value={accuracy} />
            {metrics.precision && <MetricPill label="Precision" value={metrics.precision} />}
            {metrics.recall && <MetricPill label="Recall" value={metrics.recall} />}
            {metrics.f1Score && <MetricPill label="F1 Score" value={metrics.f1Score} />}
            {metrics.auc && <MetricPill label="AUC" value={metrics.auc} />}
          </div>
        </div>
      )}

      {/* Expandable Details */}
      {expandable && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-5 py-3 flex items-center justify-between bg-surface-800/30 border-t border-surface-700/50 hover:bg-surface-800/50 transition-colors"
        >
          <span className="text-sm text-surface-400">Technical Details</span>
          {isExpanded ? <ChevronUp size={16} className="text-surface-400" /> : <ChevronDown size={16} className="text-surface-400" />}
        </button>
      )}
    </div>
  )
}

// Export variant components for convenience
export function ModelBadge(props: Omit<ModelPerformanceBadgeProps, 'variant'>) {
  return <ModelPerformanceBadge {...props} variant="badge" />
}

export function ModelCompact(props: Omit<ModelPerformanceBadgeProps, 'variant'>) {
  return <ModelPerformanceBadge {...props} variant="compact" />
}

export function ModelCard(props: Omit<ModelPerformanceBadgeProps, 'variant'>) {
  return <ModelPerformanceBadge {...props} variant="card" />
}

export function ModelDetailed(props: Omit<ModelPerformanceBadgeProps, 'variant'>) {
  return <ModelPerformanceBadge {...props} variant="detailed" />
}
